import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2 } from "lucide-react";

export default function AdminClients() {
    const [clients, setClients] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ login_id: "", name: "", company: "" });

    const load = async () => {
        const r = await api.get("/clients");
        setClients(r.data);
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.login_id.trim() || !form.name.trim()) return toast.error("Login ID and name required");
        if (!/^\d+$/.test(form.login_id)) return toast.error("Login ID must be numeric");
        try {
            await api.post("/clients", form);
            toast.success(`Client created · access code ${form.login_id}`);
            setForm({ login_id: "", name: "", company: "" });
            setOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Deactivate this client?")) return;
        await api.delete(`/clients/${id}`);
        toast.success("Deactivated");
        load();
    };

    return (
        <div className="p-4 sm:p-6 lg:p-10 max-w-6xl">
            <div className="flex items-end justify-between mb-8">
                <div>
                    <div className="overline text-neutral-500">Access</div>
                    <h1 className="font-display text-5xl tracking-tight leading-none mt-2">Clients</h1>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="rounded-none bg-black text-white hover:bg-neutral-800 uppercase tracking-widest text-xs h-11 px-5" data-testid="add-client-btn">
                            <UserPlus className="h-4 w-4 mr-2" strokeWidth={1.5}/> New Client
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-display text-2xl tracking-tight">Create Client</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label className="overline">Access Code (numeric)</Label>
                                <Input value={form.login_id} onChange={(e) => setForm({...form, login_id: e.target.value.replace(/[^0-9]/g, "")})} className="rounded-none border-black mt-2 h-11 tabular-nums" placeholder="e.g. 1003" data-testid="client-login-id-input" inputMode="numeric"/>
                                <div className="text-[11px] text-neutral-500 mt-1 font-body">The client will sign in with just this code — no separate password.</div>
                            </div>
                            <div>
                                <Label className="overline">Name</Label>
                                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-none border-black mt-2 h-11" data-testid="client-name-input"/>
                            </div>
                            <div>
                                <Label className="overline">Company</Label>
                                <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} className="rounded-none border-black mt-2 h-11" data-testid="client-company-input"/>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-black">Cancel</Button>
                            <Button onClick={submit} className="rounded-none bg-black text-white hover:bg-neutral-800" data-testid="client-save-btn">Create</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="border border-border">
                <div className="grid grid-cols-6 px-5 py-3 border-b border-border overline text-neutral-600 bg-neutral-50">
                    <div>ID</div>
                    <div className="col-span-2">Name</div>
                    <div className="col-span-2">Company</div>
                    <div className="text-right">Actions</div>
                </div>
                {clients.map((c) => (
                    <div key={c.id} className="grid grid-cols-6 px-5 py-4 border-b border-border last:border-0 items-center font-body text-sm hover:bg-neutral-50" data-testid={`client-row-${c.id}`}>
                        <div className="font-mono">{c.login_id}</div>
                        <div className="col-span-2">{c.name}</div>
                        <div className="col-span-2 text-neutral-600">{c.company || "—"}</div>
                        <div className="text-right">
                            <button onClick={() => remove(c.id)} className="text-red-700 hover:text-red-900 inline-flex items-center gap-1 uppercase tracking-widest text-[10px]" data-testid={`delete-client-${c.id}`}>
                                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5}/> Deactivate
                            </button>
                        </div>
                    </div>
                ))}
                {clients.length === 0 && <div className="p-8 text-center text-sm text-neutral-500 font-body">No clients yet</div>}
            </div>
        </div>
    );
}
